// frontend/src/components/HypeTrending.js
import React, { useEffect, useState, useMemo } from "react";
import "./HypeTrending.css";

function fmtNum(n, d = 2) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return Number(n).toFixed(d);
}

function fmtPct(n, d = 1) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const v = Number(n);
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(d)}%`;
}

function scoreColor(score) {
  if (score == null || !Number.isFinite(Number(score))) return "#9ca3af";
  const s = Number(score);
  if (s >= 0.012) return "#22c55e"; // strong
  if (s >= 0.008) return "#a3e635"; // good
  if (s >= 0.004) return "#eab308"; // ok
  if (s > 0) return "#f97316";     // weak positive
  return "#ef4444";                // negative
}

function rsiColor(rsi) {
  if (rsi == null || !Number.isFinite(Number(rsi))) return "#9ca3af";
  const v = Number(rsi);
  if (v < 35) return "#38bdf8";   // oversold-ish
  if (v < 55) return "#e5e7eb";   // neutral
  if (v < 70) return "#22c55e";   // bullish
  return "#f97316";               // hot
}

function levelLabel(level) {
  if (level === "trending-plus") return "Trending+";
  return "Trending";
}

function levelClass(level) {
  if (level === "trending-plus") return "ht-level-plus";
  return "ht-level-basic";
}

export default function HypeTrending() {
  const isLocal = window.location.hostname === "localhost";
  const baseUrl = isLocal
    ? "http://localhost:5000"
    : "https://www.trendscout.club";

  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const toggleOpen = () => setIsOpen((v) => !v);

  // Fetch trending tokens
  useEffect(() => {
    let cancelled = false;

    async function loadTrending() {
      setLoading(true);
      setError(null);
      try {
        const url = `${baseUrl}/api/hype/trending`; // adjust if needed
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        if (cancelled) return;

        const arr = Array.isArray(json)
          ? json
          : Array.isArray(json.items)
          ? json.items
          : [];

        setItems(arr);
        setLastUpdated(new Date());
      } catch (e) {
        if (cancelled) return;
        console.error("[HypeTrending] load error", e);
        setError(e.message || "Failed to load trending tokens");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTrending();

    // optional: auto-refresh every 2 minutes
    const id = setInterval(loadTrending, 2 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl]);

  // Sort: Trending+ first, then by score desc, then hits48h desc
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const lvlA = a.level === "trending-plus" ? 1 : 0;
      const lvlB = b.level === "trending-plus" ? 1 : 0;
      if (lvlB !== lvlA) return lvlB - lvlA;

      const sA = Number(a.score ?? 0);
      const sB = Number(b.score ?? 0);
      if (sB !== sA) return sB - sA;

      const hA = Number(a.hits48h ?? 0);
      const hB = Number(b.hits48h ?? 0);
      return hB - hA;
    });
  }, [items]);

  const openChart = (symbol) => {
    if (!symbol) return;
    const baseToken = String(symbol).toUpperCase().replace(/USDT$/, "");
    const tvSym = `BINANCE:${baseToken}USDT`;
    const url = `https://www.tradingview.com/chart/?symbol=${encodeURIComponent(
      tvSym
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const fmtTime = (d) => {
    if (!d) return "—";
    const dt = typeof d === "string" ? new Date(d) : d;
    if (!dt || Number.isNaN(+dt)) return "—";
    return dt.toLocaleString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className={`ht-wrapper ${isOpen ? "ht-open" : ""}`}>
      {/* Vertical tab */}
      <button className="ht-tab" onClick={toggleOpen}>
        {"TRENDING".split("").map((ch, idx) => (
          <span key={idx}>{ch}</span>
        ))}
      </button>

      {/* Drawer */}
      <div className="ht-drawer">
        <div className="ht-header">
          <div>
            <div className="ht-title">Hype Trending Radar</div>
            <div className="ht-subtitle">
              AI-approved longs aligned with Hype Scanner
            </div>
          </div>
          <button className="ht-close-btn" onClick={toggleOpen}>
            ✕
          </button>
        </div>

        <div className="ht-meta-row">
          {loading && <span className="ht-pill ht-pill-loading">Loading…</span>}
          {error && <span className="ht-pill ht-pill-error">{error}</span>}
          {!loading && !error && (
            <span className="ht-pill">
              {sortedItems.length} token
              {sortedItems.length === 1 ? "" : "s"}
            </span>
          )}
          {lastUpdated && (
            <span className="ht-asof">Updated {fmtTime(lastUpdated)}</span>
          )}
        </div>

        <div className="ht-list">
          {sortedItems.length === 0 && !loading && !error && (
            <div className="ht-empty">
              No trending tokens yet.  
              <span className="ht-empty-small">
                Fresh BUY setups that overlap with hype will appear here.
              </span>
            </div>
          )}

          {sortedItems.map((t) => {
            const base = (t.token || "").toUpperCase();
            const sym = t.symbol || `${base}USDT`;
            const lvlClass = levelClass(t.level);

            return (
              <div
                key={t._id || t.token || sym}
                className="ht-card"
                onClick={() => openChart(sym)}
                title="Open TradingView chart"
              >
                <div className="ht-card-main">
                  <div className="ht-token-row">
                    <div className="ht-token-left">
                      <span className="ht-token-symbol">{base}</span>
                      <span className={`ht-level-badge ${lvlClass}`}>
                        {levelLabel(t.level)}
                      </span>
                    </div>
                    <div className="ht-token-right">
                      <span className="ht-price">
                        ${fmtNum(t.priceCaught, 4)}
                      </span>
                    </div>
                  </div>

                  <div className="ht-row">
                    <div className="ht-col">
                      <div className="ht-label">RSI (1D)</div>
                      <div
                        className="ht-value"
                        style={{ color: rsiColor(t.rsiDaily) }}
                      >
                        {fmtNum(t.rsiDaily, 1)}
                      </div>
                    </div>
                    <div className="ht-col">
                      <div className="ht-label">Hits (48h)</div>
                      <div className="ht-value">{t.hits48h ?? 0}</div>
                    </div>
                    <div className="ht-col">
                      <div className="ht-label">Score</div>
                      <div
                        className="ht-value"
                        style={{ color: scoreColor(t.score) }}
                      >
                        {fmtNum(t.score, 4)}
                      </div>
                    </div>
                  </div>

                  <div className="ht-row">
                    <div className="ht-col ht-col-wide">
                      <div className="ht-label">Category</div>
                      <div className="ht-value ht-cat">
                        {t.categoryName || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ht-card-foot">
                  <span className="ht-foot-pill">click → chart</span>
                  {t.categorySlug && (
                    <span className="ht-foot-pill ht-foot-pill-soft">
                      {t.categorySlug}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
